import { IDataWithLocalizedName, IDataWithLocalizedTitle } from '../../contracts/localized-data.js';
import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder,
  isSameData,
  isSameList,
  SerializationContext,
  SerializedData,
} from '../../models/dto/dto.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { MenuType } from '../../models/serializable/menu/menu-type.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';
import { MenuCategory } from '../../models/serializable/menu/menu-category.js';
import { MenuItem } from '../../models/serializable/menu/menu-item.js';

export class MenuComponent extends RenderableComponent implements IDataWithLocalizedName, IDataWithLocalizedTitle {
  static readonly TYPE_ID: string = '197c127f87d04e1bbb23606a277d4c2a';
  static readonly DEFAULT_RENDER_ORDER: number = 15;
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly names: LocalizedString,
    public readonly titles: LocalizedString,
    public readonly categories: MenuCategory[],
    public readonly items: MenuItem[],
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
    public readonly menuType: MenuType,
  ) {
    super();
  }

  get $typeId(): string {
    return MenuComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, MenuComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): MenuComponent {
    return new MenuComponent(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        MapUtils.getList<MenuCategory>(
            map,
            'categories',
            (data) => context.fromData<MenuCategory>(data as SerializedData)
        ),
        MapUtils.getList<MenuItem>(map, 'items', (data) => context.fromData<MenuItem>(data as SerializedData)),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
        toEnumFromString(MapUtils.get<string>(map, 'menuType'), MenuType),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': context.toData(this.names),
      'titles': context.toData(this.titles),
      'categories': this.categories.map((e) => context.toData(e)),
      'items': this.items.map((e) => context.toData(e)),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
      'menuType': fromEnumAsString(this.menuType),
    };
  }

  clone(): this {
    return new MenuComponent(
        this.names.clone(),
        this.titles.clone(),
        this.categories.map((e) => e.clone()),
        this.items.map((e) => e.clone()),
        this.renderOrder,
        this.isRenderingEnabled,
        this.menuType,
    ) as this;
  }

  isSame(other: MenuComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.names, this.names) &&
      isSameData(other.titles, this.titles) &&
      isSameList(other.categories, this.categories, isSameData) &&
      isSameList(other.items, this.items, isSameData) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled &&
      other.menuType == this.menuType;
  }
}

export class MenuComponentBuilder extends DataModelBuilder<MenuComponent> {
  build(context: SerializationContext, map: SerializedData): MenuComponent {
    return MenuComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'menuType', MenuType, MenuType.Menu);

    if(versionRange.contains(MenuComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(MenuComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `menuType` of type `MenuType` was introduced.
    if(version < 2) {
      map['menuType'] ??= fromEnumAsString(MenuType.Menu);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
